<template>
  <div class="ai-chart">
    <div class="ai-chart__bar">
      <div
        v-if="data.series.length > 1"
        class="ai-chart__metrics"
      >
        <button
          v-for="(s, idx) in data.series"
          :key="s.name"
          type="button"
          class="ai-chart__chip"
          :class="{ 'ai-chart__chip--active': idx === seriesIndex }"
          @click="seriesIndex = idx"
        >
          {{ s.name }}
        </button>
      </div>
      <div class="ai-chart__types">
        <button
          type="button"
          class="ai-chart__chip"
          :class="{ 'ai-chart__chip--active': chartType === 'bar' }"
          @click="chartType = 'bar'"
        >
          <CIcon
            name="cil-bar-chart"
            size="sm"
          />
          Bar
        </button>
        <button
          type="button"
          class="ai-chart__chip"
          :class="{ 'ai-chart__chip--active': chartType === 'line' }"
          @click="chartType = 'line'"
        >
          Line
        </button>
      </div>
    </div>

    <div class="ai-chart__canvas">
      <component
        :is="chartComponent"
        :datasets="datasets"
        :labels="data.labels"
        :options="options"
        style="height: 240px"
      />
    </div>
  </div>
</template>

<script>
import { CChartBar, CChartLine } from '@coreui/vue-chartjs'

export default {
  name: 'AiAgentChart',

  components: { CChartBar, CChartLine },

  props: {
    // { labels: string[], series: [{ name, values: number[] }] }
    data: {
      type: Object,
      required: true,
      validator: d => d && Array.isArray(d.labels) && Array.isArray(d.series) && d.series.length > 0
    }
  },

  data() {
    return {
      seriesIndex: 0,
      chartType: 'bar'
    }
  },

  computed: {
    chartComponent() {
      return this.chartType === 'line' ? 'CChartLine' : 'CChartBar'
    },

    current() {
      return this.data.series[this.seriesIndex] || this.data.series[0]
    },

    datasets() {
      const accent = '#5b73e8'
      const isLine = this.chartType === 'line'
      return [{
        label: this.current.name,
        data: this.current.values,
        backgroundColor: isLine ? 'rgba(91, 115, 232, 0.15)' : '#5b73e8',
        borderColor: accent,
        borderWidth: 2,
        pointBackgroundColor: accent,
        pointRadius: 3,
        fill: isLine,
        tension: 0.25,
        maxBarThickness: 48
      }]
    },

    options() {
      return {
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: {
              fontColor: '#8a93a2',
              fontSize: 10,
              maxRotation: 35,
              minRotation: 0,
              autoSkip: false,
              callback: value => this.truncate(value)
            }
          }],
          yAxes: [{
            gridLines: { color: 'rgba(143, 166, 200, 0.18)', drawBorder: false },
            ticks: {
              fontColor: '#8a93a2',
              fontSize: 10,
              beginAtZero: true,
              callback: value => this.compact(value)
            }
          }]
        },
        tooltips: {
          backgroundColor: 'rgba(15, 19, 32, 0.92)',
          titleFontColor: '#fff',
          bodyFontColor: '#fff',
          cornerRadius: 6,
          callbacks: {
            label: item => `${this.current.name}: ${this.compact(item.yLabel)}`
          }
        }
      }
    }
  },

  watch: {
    data() {
      // Reset the selected metric when a different message's data is bound.
      this.seriesIndex = 0
    }
  },

  methods: {
    truncate(label) {
      const s = String(label)
      return s.length > 16 ? s.slice(0, 15) + '…' : s
    },

    compact(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) {
        return value
      }

      const abs = Math.abs(n)
      if (abs >= 1_000_000) {
        return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
      }

      if (abs >= 1_000) {
        return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
      }

      return String(n)
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-chart {
  margin-top: 10px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e9ecf3;
  border-radius: 10px;

  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__metrics,
  &__types {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__chip {
    appearance: none;
    border: 1px solid #e9ecf3;
    background: #f6f8fd;
    color: #5a6782;
    font-size: 0.72rem;
    font-weight: 600;
    font-family: inherit;
    padding: 3px 9px;
    border-radius: 999px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &:hover { background: #eef0fd; color: #1e2b45; }

    &--active {
      background: #5b73e8;
      border-color: #5b73e8;
      color: #fff;

      &:hover { background: #4a60d4; color: #fff; }
    }
  }

  &__canvas {
    position: relative;
    width: 100%;
  }
}
</style>
